import { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Text, Heading, LabeledValue } from "@react-spectrum/s2";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import {
  TemplateColorAsset,
  TemplateImageAsset,
  getExtendedTemplateAssets,
} from "./TemplateAsset";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import { headingStyle } from "./style" with { type: "macro" };

const NO_ASSET_NAMES = [];

/**
 * The template's branding assets (colors, logo, app icon) and campaign visuals,
 * each filled by the user. `excludedAssetNames` are left out — rendered
 * elsewhere, e.g. by the step's `campaignStructure`.
 */
export const TemplateAssets = ({ excludedAssetNames = NO_ASSET_NAMES }) => {
  const {
    video,
    template,
    selectedCampaign, // keep the import, needed for an `eval()`
    scrapedColors: colors = [],
    handleUploadAssets: onDrop,
    handleColorChange: onColorChange,
    handleAssetMenuAction,
  } = useContext(VideoWizardContext);

  // What the use-case `condition`s are evaluated with.
  const scope = { video, template, selectedCampaign };

  const templateAssets = useMemo(
    () => template.data?.assets || [],
    [template.data?.assets],
  );

  const shownAssets = useMemo(
    () =>
      templateAssets.filter(
        (asset) => !excludedAssetNames.includes(asset.name),
      ),
    [templateAssets, excludedAssetNames],
  );

  const extendedAssets = useMemo(
    () => getExtendedTemplateAssets(shownAssets, video.data?.assets),
    [shownAssets, video.data?.assets],
  );

  const brandAssets = useMemo(
    () =>
      Object.values(extendedAssets).filter((asset) =>
        ["Logo", "App-icon"].includes(asset.name),
      ) || [],
    [extendedAssets],
  );

  const visualAssets = useMemo(
    () =>
      Object.values(extendedAssets).filter(
        (asset) =>
          asset.type === "image" && !["Logo", "App-icon"].includes(asset.name),
      ) || [],
    [extendedAssets],
  );

  const onDelete = (asset) => handleAssetMenuAction("delete", asset);

  return (
    <>
      <div className={style({ display: "flex", flexDirection: "column" })}>
        {!!brandAssets?.length && (
          <Heading styles={style({ ...headingStyle(), font: "heading-xs" })}>
            <Text>Branding</Text>
          </Heading>
        )}

        <div
          className={style({
            display: "flex",
            // flexWrap: "wrap",
            gap: 32,
          })}
        >
          {shownAssets
            .filter((asset) => asset.type === "color")
            .map((asset) => (
              <TemplateColorAsset
                key={asset.name}
                asset={asset}
                selectedAsset={extendedAssets[asset.name]}
                // The swatches are too narrow for a description.
                description={null}
                scope={scope}
                colors={colors}
                onChange={onColorChange}
              />
            ))}

          {brandAssets.map((asset) => (
            <TemplateImageAsset
              key={asset.name}
              asset={asset}
              selectedAsset={asset}
              scope={scope}
              showDetails={false}
              onDrop={onDrop}
              styles={style({
                flexBasis: "[15rem]",
                flexGrow: 0,
                flexShrink: 1,
              })}
            />
          ))}
        </div>

        {visualAssets?.length > 0 && (
          <Heading
            styles={style(
              headingStyle({ size: "xs", marginTop: 64, marginBottom: 0 }),
            )}
          >
            <Text>Campaign visuals</Text>
          </Heading>
        )}

        {video.data?.templateId === "csc-overview" && (
          <div
            className={style({
              display: "flex",
              gap: 8,
              marginTop: -8,
              marginBottom: 24,
              font: "ui",
              color: "neutral-subdued",
            })}
          >
            <Text>Campaign focus:</Text>
            <Text styles={style({ fontWeight: "bold" })}>
              {
                video.data?.campaigns?.find(
                  (c) => c.id === video.data?.selectedCampaignIds?.[0],
                )?.title
              }
            </Text>
          </div>
        )}

        <div
          className={style({
            display: "flex",
            flexWrap: "wrap",
            gap: 32,
          })}
        >
          {visualAssets.map((asset) => (
            <TemplateImageAsset
              key={asset.name}
              asset={asset}
              selectedAsset={asset}
              scope={scope}
              onDrop={onDrop}
              onDelete={onDelete}
            >
              {asset?.context?.map(
                (contextItem, cix) =>
                  !!eval(contextItem?.value) && (
                    <LabeledValue
                      key={cix}
                      label={contextItem.label}
                      size="S"
                      value={
                        <Text styles={style({ font: "ui" })}>
                          {eval(contextItem.value)}
                        </Text>
                      }
                    />
                  ),
              )}
            </TemplateImageAsset>
          ))}
        </div>
      </div>
    </>
  );
};

TemplateAssets.propTypes = {
  excludedAssetNames: PropTypes.arrayOf(PropTypes.string),
};
